import { useCallback, useEffect, useRef, useState } from 'react';
import type { EditedWorkspaceSnapshot } from '../Reports/addFileToReports';

export type WorkspaceSyncStatus = 'idle' | 'unsaved' | 'saved';

/** How the user answered the unsaved-edits prompt (analytics). */
export type UnsavedEditsPromptAction = 'SAVED' | 'DISCARDED' | 'CANCELLED';

export interface UseUnsavedEditsGuardArgs {
    /** Off in contexts that persist on their own (the Reports sheet editor autosaves). */
    enabled: boolean;
    /** The screen differs from the last confirmed Workspace save (or from the original if never added). */
    hasUnsavedEdits: boolean;
    /** Sticky — the screen differs from the original file; an add never resets it. */
    hasEditedSinceOpen: boolean;
    /** Live reads of the two flags above, for drawers whose last edit can land
     *  during `commitPendingEdit` (after this render's props were taken). */
    getHasUnsavedEdits?: () => boolean;
    getHasEditedSinceOpen?: () => boolean;
    /** Commits an edit still in progress (a cell being typed into), then runs
     *  `then` — so a close or add sees it. Defaults to running `then` at once. */
    commitPendingEdit?: (then: () => void) => void;
    /** Monotonic content-edit counter — lets a successful add tell whether an edit landed after its snapshot. */
    getEditVersion: () => number;
    /** Builds the on-screen content to hand to the host — only called when there are edits. */
    buildSnapshot: () => EditedWorkspaceSnapshot;
    /** Flips the drawer's dirty state back to clean once the host confirms the add. */
    clearUnsavedEdits: () => void;
    onClose: () => void;
    onAddToReports?: (edited?: EditedWorkspaceSnapshot) => void;
    addingToReports?: boolean;
    addedToReports?: boolean;
    /** Told how the prompt was answered. SAVED is the user's choice; whether
     *  that add then succeeded is the host's own add outcome. */
    onPromptResolved?: (action: UnsavedEditsPromptAction) => void;
}

export interface UseUnsavedEditsGuardResult {
    /** Use in place of `onClose` for every close path (×, ESC, backdrop, Back). */
    requestClose: () => void;
    /** The header's Add to Workspace click — hands the host the edited snapshot, or nothing when clean. */
    addToWorkspace: () => void;
    /** True while a close would prompt — the drawer then also guards browser Back. */
    guardActive: boolean;
    /** Drives the header's saved/unsaved indicator. */
    syncStatus: WorkspaceSyncStatus;
    dialog: {
        open: boolean;
        /** An add is in flight (started from the header): the dialog's Add waits for it. */
        adding: boolean;
        onKeepEditing: () => void;
        onDiscard: () => void;
        onAddToWorkspace?: () => void;
    };
}

const runNow = (then: () => void): void => then();

/**
 * Shared "edits must reach Workspace" behaviour for the editable file drawers
 * (SheetEditorDrawer in a view context, DocxDrawer):
 *
 * - `addToWorkspace` passes the live content to the host's `onAddToReports`
 *   once the user has edited (even after an earlier add), and `undefined`
 *   (→ snapshot the original bytes, exactly as before) while the file is
 *   identical to the original (never edited since open).
 * - `requestClose` intercepts every close with unsaved edits and asks the
 *   user to add / keep editing / discard. "Add to Workspace" from that dialog
 *   closes the drawer only once the host reports success; a failed add leaves
 *   the editor open with the edits intact.
 * - Both first commit an edit still in progress, then read the LIVE dirty
 *   state, so text typed into a cell but not yet entered is neither silently
 *   dropped on close nor left out of the snapshot.
 * - The host owns the async add and signals its outcome through the existing
 *   `addingToReports` / `addedToReports` props, so no new callback contract is
 *   needed: a run that ends with `addedToReports` true succeeded, one that
 *   ends without it failed (hosts reset `addedToReports` when an add starts).
 * - A successful add clears "unsaved" only if no edit landed after the snapshot.
 */
export const useUnsavedEditsGuard = ({
    enabled,
    hasUnsavedEdits,
    hasEditedSinceOpen,
    getHasUnsavedEdits,
    getHasEditedSinceOpen,
    commitPendingEdit = runNow,
    getEditVersion,
    buildSnapshot,
    clearUnsavedEdits,
    onClose,
    onAddToReports,
    addingToReports,
    addedToReports,
    onPromptResolved,
}: UseUnsavedEditsGuardArgs): UseUnsavedEditsGuardResult => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [savedToWorkspace, setSavedToWorkspace] = useState(false);
    // Set by the dialog's "Add to Workspace": close once the host confirms.
    const pendingCloseRef = useRef(false);
    // Observed an add start — the next `addingToReports:false` is its outcome.
    const sawAddingRef = useRef(false);
    // Edit counter captured when the add started — compared on success.
    const snapshotVersionRef = useRef<number | null>(null);
    // Set when the host is called, cleared on the next render: the props below (`addingToReports`)
    // can't yet reflect an add started in this same tick, and a second add in that window would
    // overwrite `snapshotVersionRef` and could count an edit made in between as saved.
    const addInFlightRef = useRef(false);
    useEffect(() => {
        addInFlightRef.current = false;
    });

    // Render-time props mirrored for the deferred (post-commit) callbacks.
    const latest = useRef({ enabled, hasUnsavedEdits, hasEditedSinceOpen, onClose, onAddToReports, addingToReports });
    latest.current = { enabled, hasUnsavedEdits, hasEditedSinceOpen, onClose, onAddToReports, addingToReports };
    const unsavedNow = useCallback(
        () => (getHasUnsavedEdits ? getHasUnsavedEdits() : latest.current.hasUnsavedEdits),
        [getHasUnsavedEdits],
    );
    const editedNow = useCallback(
        () => (getHasEditedSinceOpen ? getHasEditedSinceOpen() : latest.current.hasEditedSinceOpen),
        [getHasEditedSinceOpen],
    );

    const guardActive = enabled && hasUnsavedEdits;

    /** Starts an add once any in-progress edit is committed. `closeAfter` closes the drawer when the
     *  host confirms it; `onStarted` runs only if the add really starts. */
    const startAdd = useCallback(
        (closeAfter: boolean, onStarted?: () => void): void => {
            // One add at a time: a second one would overwrite the edit version the first is judged by
            // (an edit made in between would then count as saved), and a host without a busy flag
            // would add the file twice.
            if (!latest.current.onAddToReports || latest.current.addingToReports || addInFlightRef.current) return;
            commitPendingEdit(() => {
                const add = latest.current.onAddToReports;
                // Checked again: the commit may have been deferred, and an add may have started meanwhile.
                if (!add || latest.current.addingToReports || addInFlightRef.current) return;
                addInFlightRef.current = true;
                snapshotVersionRef.current = getEditVersion();
                // Set only once the add really starts, so a declined one can never close the drawer later.
                pendingCloseRef.current = closeAfter;
                onStarted?.();
                add(editedNow() ? buildSnapshot() : undefined);
            });
        },
        [commitPendingEdit, getEditVersion, editedNow, buildSnapshot],
    );
    const addToWorkspace = useCallback(() => {
        startAdd(false);
    }, [startAdd]);

    const requestClose = useCallback(() => {
        // Off (e.g. the Reports editor, which commits and flushes on its own
        // close path): close straight through, no extra commit.
        if (!latest.current.enabled) {
            latest.current.onClose();
            return;
        }
        commitPendingEdit(() => {
            if (latest.current.enabled && unsavedNow()) {
                setDialogOpen(true);
                return;
            }
            latest.current.onClose();
        });
    }, [commitPendingEdit, unsavedNow]);

    useEffect(() => {
        if (addingToReports) {
            sawAddingRef.current = true;
            return;
        }
        if (!sawAddingRef.current) return;
        sawAddingRef.current = false;
        const caughtUp = snapshotVersionRef.current !== null && getEditVersion() === snapshotVersionRef.current;
        snapshotVersionRef.current = null;
        if (addedToReports && caughtUp) {
            clearUnsavedEdits();
            setSavedToWorkspace(true);
            // A prompt opened during the add has nothing left to ask (its Add would add the file again).
            setDialogOpen(false);
            if (pendingCloseRef.current) {
                pendingCloseRef.current = false;
                onClose();
            }
            return;
        }
        // The add failed, or an edit landed mid-flight — stay open, edits stay unsaved (the host toasts a failure).
        pendingCloseRef.current = false;
    }, [addingToReports, addedToReports, clearUnsavedEdits, getEditVersion, onClose]);

    // A fresh edit after a successful add flips the indicator back to unsaved.
    useEffect(() => {
        if (hasUnsavedEdits) setSavedToWorkspace(false);
    }, [hasUnsavedEdits]);

    // Browser tab close / reload with unsaved edits — the drawer can't show its
    // own dialog there, so lean on the native prompt.
    useEffect(() => {
        if (!guardActive) return undefined;
        const warn = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [guardActive]);

    const syncStatus: WorkspaceSyncStatus = !enabled ? 'idle' : hasUnsavedEdits ? 'unsaved' : savedToWorkspace ? 'saved' : 'idle';

    return {
        requestClose,
        addToWorkspace,
        guardActive,
        syncStatus,
        dialog: {
            open: dialogOpen,
            adding: Boolean(addingToReports),
            onKeepEditing: () => {
                setDialogOpen(false);
                onPromptResolved?.('CANCELLED');
            },
            onDiscard: () => {
                setDialogOpen(false);
                onPromptResolved?.('DISCARDED');
                onClose();
            },
            onAddToWorkspace: onAddToReports
                ? () =>
                      // Resolved only once the add really starts. While one is in flight (the button
                      // says so) the prompt stays: that add's outcome closes it or leaves it usable.
                      startAdd(true, () => {
                          setDialogOpen(false);
                          onPromptResolved?.('SAVED');
                      })
                : undefined,
        },
    };
};
